import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSession } from '@/api/session-context';
import type { Theme } from '@/hooks/useTheme';
import { ROUTES } from '@/router/paths';

import MobileNav from './MobileNav';
import ZoneDrum from './ZoneDrum';
import {
  ZONES,
  getDefaultZonePath,
  getRememberedNavItemPath,
  getRememberedZonePath,
  getZoneFromPath,
  rememberNavItemPath,
  rememberZonePath,
  type NavItem,
  type ZoneId,
} from './zones';

interface TopNavProps {
  onOpenCommand: () => void;
  theme: Theme;
  onToggleTheme: () => void;
}

export default function TopNav({ onOpenCommand, theme, onToggleTheme }: TopNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const activeZone = getZoneFromPath(location.pathname);
  const isDark = theme === 'dark';

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const logoutPendingRef = useRef(false);
  const navRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!mobileOpen) return;
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (event.target instanceof Element && event.target.closest('dialog[open]')) return;
      setMobileOpen(false);
      mobileButtonRef.current?.focus();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [mobileOpen]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const breakpoint = window.matchMedia('(min-width: 768px)');
    const onBreakpoint = () => {
      if (!breakpoint.matches) return;
      const focusWasMobile =
        document.activeElement === mobileButtonRef.current ||
        document.activeElement?.closest('#mobile-navigation');
      setMobileOpen(false);
      if (focusWasMobile) navRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    };
    onBreakpoint();
    breakpoint.addEventListener('change', onBreakpoint);
    return () => breakpoint.removeEventListener('change', onBreakpoint);
  }, []);

  const activeItems = useMemo(
    () => ZONES.find((z) => z.id === activeZone)?.items ?? [],
    [activeZone],
  );

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isItemActive = (item: NavItem) =>
    item.children ? item.children.some((c) => isActive(c.path)) : isActive(item.path);

  useLayoutEffect(() => {
    const nav = navRef.current;
    const measure = measureRef.current;
    if (!nav || !measure) return;
    const update = () => setCompact(measure.scrollWidth > nav.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [activeItems]);

  useEffect(() => {
    if (!openDropdown) return;
    const onClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openDropdown]);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    if (!location.pathname.startsWith(`${ROUTES.analytics.wordstat}/`)) {
      rememberZonePath(activeZone, currentPath);
      rememberNavItemPath(currentPath);
    }
  }, [activeZone, location.hash, location.pathname, location.search]);

  const handleLogout = async () => {
    if (logoutPendingRef.current) return;

    logoutPendingRef.current = true;
    setLogoutPending(true);
    try {
      await session.logout();
    } catch {
      // The controller deliberately ends the local session even if revocation fails.
    } finally {
      logoutPendingRef.current = false;
      setLogoutPending(false);
    }
  };

  const handleZoneSwitch = (id: ZoneId) => {
    navigate(getRememberedZonePath(id) ?? getDefaultZonePath(id));
  };

  const handleNavItemNavigate = (path: string) => {
    setMobileOpen(false);
    navigate(getRememberedNavItemPath(path) ?? path);
  };

  const handleOpenCommand = () => {
    setMobileOpen(false);
    onOpenCommand();
  };

  const baseBtn =
    'flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer';
  const activeCls = 'bg-accent-50 text-accent-700 shadow-[inset_0_-2px_0_0_#ca0404]';
  const inactiveCls = 'text-surface-500 hover:bg-surface-100 hover:text-surface-900';

  return (
    <header className="app-header relative z-10 flex min-h-0 flex-col border-b border-surface-200 bg-white/95 backdrop-blur-md transition-colors duration-200 dark:bg-surface-100/95">
      <div className="mx-auto flex h-[60px] w-full max-w-[1440px] shrink-0 items-center gap-2 px-4 sm:px-6">
        <div className="flex items-center shrink-0">
          <button
            type="button"
            onClick={() => navigate(ROUTES.analytics.dynamics)}
            className="group flex items-center gap-2.5 pr-3 cursor-pointer"
            aria-label="Перейти на главную"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-white transition-transform group-hover:-translate-y-0.5">
              <i className="ri-rocket-line text-sm" />
            </span>
            <span className="whitespace-nowrap text-base font-semibold tracking-[-0.02em] text-surface-900 transition-colors">
              Queries
            </span>
          </button>

          <div className="hidden md:block">
            <ZoneDrum activeZone={activeZone} onSwitch={handleZoneSwitch} />
          </div>
        </div>

        <span className="mx-1 hidden h-5 w-px shrink-0 bg-surface-200 transition-colors md:block" />

        <nav
          ref={navRef}
          aria-label="Основная навигация"
          className="hidden min-w-0 flex-1 items-center gap-1 overflow-visible md:flex"
        >
          {activeItems.map((item) => {
            const active = isItemActive(item);

            if (item.children) {
              const open = openDropdown === item.path;
              return (
                <div key={item.path} className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(open ? null : item.path)}
                    title={item.label}
                    className={`${baseBtn} ${active ? activeCls : inactiveCls}`}
                    aria-expanded={open}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    {(!compact || active) && <span>{item.label}</span>}
                    {(!compact || active) && (
                      <i
                        className={`ri-arrow-down-s-line text-xs transition-transform ${
                          open ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </button>

                  {open && (
                    <div className="raised-panel absolute left-0 top-full z-50 mt-1.5 w-52 rounded-lg border border-surface-200 p-1.5">
                      {item.children.map((child) => {
                        const childActive = isActive(child.path);
                        return (
                          <button
                            type="button"
                            key={child.path}
                            onClick={() => {
                              setOpenDropdown(null);
                              handleNavItemNavigate(child.path);
                            }}
                            className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                              childActive
                                ? 'bg-accent-50 text-accent-700'
                                : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900'
                            }`}
                          >
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className={`${child.icon} text-base`} />
                            </span>
                            {child.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <button
                type="button"
                key={item.path}
                onClick={() => handleNavItemNavigate(item.path)}
                title={item.label}
                className={`${baseBtn} ${active ? activeCls : inactiveCls}`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-base`} />
                </span>
                {(!compact || active) && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div
          ref={measureRef}
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 -z-10 hidden gap-1 whitespace-nowrap invisible md:flex"
        >
          {activeItems.map((item) => (
            <span key={item.path} className="flex items-center gap-2 px-3 py-2 text-sm font-medium">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${item.icon} text-base`} />
              </span>
              {item.label}
              {item.children && <i className="ri-arrow-down-s-line text-xs" />}
            </span>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2.5 shrink-0">
          <button
            type="button"
            onClick={handleOpenCommand}
            aria-keyshortcuts="Control+K Meta+K"
            className="hidden h-9 items-center gap-2 rounded-md border border-surface-200 px-3 text-sm text-surface-500 transition-colors whitespace-nowrap cursor-pointer hover:border-surface-300 hover:bg-surface-100 hover:text-surface-900 sm:flex"
          >
            <i className="ri-search-line" aria-hidden="true" />
            <span>Поиск</span>
            <kbd
              aria-hidden="true"
              className="rounded border border-surface-200 bg-surface-100 px-1.5 py-0.5 font-mono text-[11px] text-surface-500"
            >
              Ctrl/⌘ K
            </kbd>
          </button>

          <button
            type="button"
            onClick={onToggleTheme}
            title={isDark ? 'Светлая тема' : 'Тёмная тема'}
            aria-label={isDark ? 'Включить светлую тему' : 'Включить тёмную тему'}
            className="flex h-9 w-9 items-center justify-center rounded-md text-surface-500 transition-colors cursor-pointer hover:bg-surface-100 hover:text-surface-900"
          >
            <i className={`${isDark ? 'ri-sun-line' : 'ri-moon-line'}`} />
          </button>

          <button
            type="button"
            onClick={() => void handleLogout()}
            disabled={logoutPending}
            aria-busy={logoutPending}
            className="flex h-9 w-9 items-center justify-center rounded-md text-surface-500 transition-colors hover:bg-surface-100 hover:text-surface-900"
            title="Выйти"
            aria-label="Выйти"
          >
            <i
              className={logoutPending ? 'ri-loader-4-line animate-spin' : 'ri-logout-box-r-line'}
              aria-hidden="true"
            />
          </button>

          <button
            ref={mobileButtonRef}
            type="button"
            onClick={() => {
              setMobileOpen((value) => !value);
              if (mobileOpen) mobileButtonRef.current?.focus();
            }}
            className="flex h-9 w-9 items-center justify-center rounded-md text-surface-500 transition-colors hover:bg-surface-100 hover:text-surface-900 md:hidden"
            aria-controls="mobile-navigation"
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? 'Закрыть меню' : 'Открыть меню'}
          >
            <i className={mobileOpen ? 'ri-close-line' : 'ri-menu-line'} aria-hidden="true" />
          </button>
        </div>
      </div>

      <MobileNav
        open={mobileOpen}
        isActive={isActive}
        onNavigate={handleNavItemNavigate}
        onOpenCommand={handleOpenCommand}
      />
    </header>
  );
}
